/**
 * Scroll effects (custom)
 *  1. Hero height changes smoothly with the scroll wheel / touch / keyboard scroll.
 *  2. Header text (menu, logo, actions) animates with scroll.
 *
 * Both effects only write CSS custom properties; all visuals live in CSS
 * (sections/hero.liquid and sections/header.liquid).
 *
 *   --hero-scroll-progress   0 → 1   (set on  .hero[data-scroll-height])
 *   --header-text-progress   0 → 1   (set on  #header-component[data-scroll-text])
 */
(() => {
  if (window.__scrollEffectsLoaded) return;
  window.__scrollEffectsLoaded = true;

  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const clamp = (v, min, max) => Math.min(max, Math.max(min, v));

  /** Smoothed values so the wheel feels fluid instead of stepped. */
  const state = new WeakMap();
  let frame = 0;
  let heroTops = new WeakMap();

  const getHero = () => document.querySelector('.hero[data-scroll-height]');
  const getHeader = () => document.querySelector('#header-component[data-scroll-text]');

  function measureHero(hero) {
    // Document position of the hero's top edge (not affected by the hero's own height changes).
    heroTops.set(hero, hero.getBoundingClientRect().top + window.scrollY);
  }

  function step(el, target, prop) {
    const s = state.get(el) ?? { value: target };
    // Ease toward the target. Jump straight there for reduced-motion users.
    s.value = reduceMotion.matches ? target : s.value + (target - s.value) * 0.18;
    if (Math.abs(target - s.value) < 0.001) s.value = target;
    state.set(el, s);
    el.style.setProperty(prop, s.value.toFixed(4));
    return s.value !== target;
  }

  function update() {
    frame = 0;
    let needsAnotherFrame = false;
    const scrollY = window.scrollY;

    const hero = getHero();
    if (hero) {
      if (!heroTops.has(hero)) measureHero(hero);
      const distance = Math.max(1, parseFloat(hero.dataset.scrollDistance || '60') / 100) * window.innerHeight;
      const target = clamp((scrollY - heroTops.get(hero)) / distance, 0, 1);
      needsAnotherFrame ||= step(hero, target, '--hero-scroll-progress');
    }

    const header = getHeader();
    if (header) {
      const distance = Math.max(50, parseFloat(header.dataset.scrollTextDistance || '240'));
      const target = clamp(scrollY / distance, 0, 1);
      needsAnotherFrame ||= step(header, target, '--header-text-progress');
    }

    if (needsAnotherFrame) request();
  }

  function request() {
    if (!frame) frame = requestAnimationFrame(update);
  }

  function remeasure() {
    heroTops = new WeakMap();
    request();
  }

  window.addEventListener('scroll', request, { passive: true });
  window.addEventListener('resize', remeasure, { passive: true });
  window.addEventListener('load', remeasure);
  reduceMotion.addEventListener?.('change', request);

  // Theme editor / section re-render support.
  document.addEventListener('shopify:section:load', remeasure);
  document.addEventListener('shopify:section:unload', remeasure);

  // First paint.
  request();
})();
